import { useEffect, useMemo, useState } from 'react';
import { UseRecurlyInstance } from '@recurly/react-recurly';
import { datadogLogs } from '@datadog/browser-logs';
import { ApplePayInstance } from 'recurly.js';

import { loadApplePay, showApplePay } from '../utils/applePay';

interface ApplePayProps {
  recurly: UseRecurlyInstance;
  checkoutTotal?: number;
  onApplePayVerify: (token: string) => void;
  onApplePayError: (error: string) => void;
  onApplePayCancel: () => void;
  flags: any;
}

export const APPLE_PAY_POLICY_LABEL =
  // eslint-disable-next-line max-len
  'This will be your method of payment for all of your purchases including your Seasonal or Annual membership fees and sale purchases.';
const APPLE_PAY_SUCCESS = 'Apple Pay payment success';
const APPLE_PAY_ERROR = 'Apple Pay error:';
const APPLE_PAY_FLOW_CANCELED = 'Apple Pay flow canceled';

const useApplePay = ({
  recurly,
  checkoutTotal,
  onApplePayVerify,
  onApplePayError,
  onApplePayCancel,
  flags,
}: ApplePayProps) => {
  const [applePayLoaded, setApplePayLoaded] = useState(false);
  const [showApplePayOption, setShowApplePayOption] = useState(false);
  const applePay = useMemo<ApplePayInstance>(() => {
    console.log(`Checkout total in Apple Pay: ${checkoutTotal}`);
    const applePayInstance = recurly.ApplePay({
      country: 'US',
      currency: 'USD',
      label: APPLE_PAY_POLICY_LABEL,
      total: checkoutTotal?.toString() || '0',
    });
    window.applePay = applePayInstance;
    return applePayInstance;
  }, [checkoutTotal]);

  useEffect(() => {
    const isApplePayAvailable = showApplePay(flags, setShowApplePayOption, showApplePayOption);
    if (!applePayLoaded) {
      loadApplePay(isApplePayAvailable, setApplePayLoaded);
    }
  }, [applePayLoaded]);

  useEffect(() => {
    if (applePay) {
      applePay.on('token', (token: any) => {
        datadogLogs.logger.info(APPLE_PAY_SUCCESS);
        console.log(`Apple Pay token success: ${token.id}`);
        onApplePayVerify(token.id);
      });

      applePay.on('error', (err) => {
        datadogLogs.logger.error(APPLE_PAY_ERROR, {}, err);
        console.log(`Apple Pay error: ${JSON.stringify(err)}`);
        onApplePayError(err.message);
      });

      applePay.on('cancel', () => {
        datadogLogs.logger.info(APPLE_PAY_FLOW_CANCELED);
        console.log('Apple Pay canceled');
        onApplePayCancel();
      });
    }
  }, [applePay]);
  return { showApplePayOption };
};

export default useApplePay;
